import React, { useState } from 'react';
import {
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Activity,
  FileText,
  RefreshCw,
  Power,
  Shield,
  Zap,
  Globe,
  Sliders,
  DollarSign,
  Receipt,
  RotateCcw,
  Check,
  Tag,
  ArrowRight,
  TrendingUp,
  Lock,
  Smartphone
} from 'lucide-react';
import { AppView } from '../../types';
import {
  INITIAL_MONETIZATION_CONTROL,
  BILLING_EXECUTIVE_METRICS,
  BILLING_PRODUCTS,
  MOCK_SUBSCRIPTIONS,
  MOCK_PAYMENT_TRANSACTIONS,
  MOCK_INVOICES,
  MOCK_COUPONS,
  PROVIDER_CAPABILITIES,
  MOCK_RECONCILIATION_LOGS
} from '../../data/billingMockData';
import {
  PaidMonetizationControl,
  PaidMonetizationMode,
  PaymentProviderType,
  BillingProduct,
  CanonicalSubscription,
  PaymentTransaction
} from '../../services/billing/billingTypes';

type BillingCommandTab =
  | 'overview'
  | 'master-switch'
  | 'products-pricing'
  | 'subscriptions'
  | 'transactions'
  | 'invoices'
  | 'providers'
  | 'coupons'
  | 'reconciliation';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const BillingCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<BillingCommandTab>('overview');
  const [monetizationControl, setMonetizationControl] = useState<PaidMonetizationControl>(INITIAL_MONETIZATION_CONTROL);
  const [switchNotice, setSwitchNotice] = useState<string | null>(null);

  // Mode Change Handler
  const handleSetMonetizationMode = (newMode: PaidMonetizationMode) => {
    setMonetizationControl((prev) => ({
      ...prev,
      mode: newMode,
      checkoutEnabled: newMode !== 'OFF',
      subscriptionsEnabled: newMode !== 'OFF',
      lastChangedAt: new Date().toISOString(),
      lastChangedBy: 'Super Admin & Chief Revenue Officer'
    }));

    let message = '';
    if (newMode === 'OFF') {
      message = 'Paid Monetization set to OFF. Free calculators & APIs remain 100% active. Zero customer paywalls.';
    } else if (newMode === 'INTERNAL_TEST') {
      message = 'Paid Monetization set to INTERNAL_TEST. Test checkout accessible only by allowlisted staff.';
    } else if (newMode === 'LIMITED_ROLLOUT') {
      message = 'Paid Monetization set to LIMITED_ROLLOUT for configured pilot markets.';
    } else {
      message = 'Paid Monetization set to ON in Production with live provider checkouts.';
    }

    setSwitchNotice(message);
    setTimeout(() => setSwitchNotice(null), 5000);
  };

  const handleToggleEmergencyKillSwitch = () => {
    const nextState = !monetizationControl.emergencyKillSwitch;
    setMonetizationControl((prev) => ({
      ...prev,
      emergencyKillSwitch: nextState,
      checkoutEnabled: !nextState && prev.mode !== 'OFF'
    }));
    setSwitchNotice(nextState ? 'EMERGENCY KILL SWITCH ACTIVATED: All checkouts paused.' : 'Emergency Kill Switch deactivated.');
    setTimeout(() => setSwitchNotice(null), 5000);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-[#1E40AF] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <CreditCard className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Revenue Operations</span>
            <span>/</span>
            <span className="text-white font-bold">Global Billing & Monetization Command Center (Prompt 21)</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                Billing, Payments & Subscriptions
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Stripe, Moneroo & Chariow provider abstraction, canonical subscription engine, usage billing, tax governance, and master monetization control.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onNavigateToView('customer-billing-portal')}
                className="px-4 py-2.5 bg-blue-900/60 hover:bg-blue-900 border border-blue-400/40 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Receipt className="w-4 h-4 text-[#38BDF8]" />
                <span>Customer Billing Portal</span>
              </button>

              <div className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border ${
                monetizationControl.mode === 'OFF'
                  ? 'bg-amber-950/60 border-amber-500/40 text-amber-300'
                  : 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
              }`}>
                <Power className="w-4 h-4" />
                <span>Master Monetization: {monetizationControl.mode}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Sub Navigation Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'overview', label: 'Executive Revenue Telemetry', icon: Activity },
              { id: 'master-switch', label: 'Paid Monetization Master Switch', icon: Power },
              { id: 'products-pricing', label: `Products & Plans (${BILLING_PRODUCTS.length})`, icon: Layers },
              { id: 'subscriptions', label: `Subscriptions (${MOCK_SUBSCRIPTIONS.length})`, icon: RefreshCw },
              { id: 'transactions', label: `Payments & Checkout (${MOCK_PAYMENT_TRANSACTIONS.length})`, icon: DollarSign },
              { id: 'invoices', label: `Invoices & Receipts (${MOCK_INVOICES.length})`, icon: FileText },
              { id: 'providers', label: `Payment Providers (${PROVIDER_CAPABILITIES.length})`, icon: Globe },
              { id: 'coupons', label: 'Coupons & Discounts', icon: Tag },
              { id: 'reconciliation', label: 'Reconciliation & Audit', icon: Shield },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as BillingCommandTab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {switchNotice && (
            <div className="p-4 mb-6 bg-blue-50 border border-blue-200 text-blue-950 rounded-xl text-sm font-bold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0" />
              <span>{switchNotice}</span>
            </div>
          )}

          {/* TAB 1: EXECUTIVE REVENUE TELEMETRY */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Policy Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-4">
                <Shield className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-blue-950 font-bold text-base mb-1">
                    CANONICAL BILLING DOMAIN & ZERO PROVIDER LOCK-IN
                  </h3>
                  <p className="text-blue-900 text-sm leading-relaxed">
                    YamooPay maintains its own immutable product catalog, price versions, canonical subscription state machine, and durable usage ledger. Stripe, Moneroo, and Chariow serve purely as payment adapters.
                  </p>
                </div>
              </div>

              {/* KPI Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Active Subscribers</div>
                  <div className="text-3xl font-black text-[#0F172A]">{BILLING_EXECUTIVE_METRICS.activePaidSubscribers}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">Stripe, Moneroo & Chariow</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Monthly Recurring (MRR)</div>
                  <div className="text-3xl font-black text-[#1E40AF]">{BILLING_EXECUTIVE_METRICS.mrrUsd}</div>
                  <div className="text-xs text-[#64748B] font-bold mt-1">ARR: {BILLING_EXECUTIVE_METRICS.arrUsd}</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Payment Success Rate</div>
                  <div className="text-3xl font-black text-[#059669]">{BILLING_EXECUTIVE_METRICS.paymentSuccessRatePct}%</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">Refund Rate: {BILLING_EXECUTIVE_METRICS.refundRatePct}%</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Readiness Scorecard</div>
                  <div className="text-3xl font-black text-[#0F172A]">{BILLING_EXECUTIVE_METRICS.readinessScorecardPct}%</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">100% reconciled ledger</div>
                </div>
              </div>

              {/* Provider Health Row */}
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">Integrated Payment Adapters</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {PROVIDER_CAPABILITIES.map((p) => (
                    <div key={p.provider} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold text-base text-[#0F172A]">{p.name}</h4>
                          <span className="text-xs text-[#64748B] font-mono">{p.provider}</span>
                        </div>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                          {p.status}
                        </span>
                      </div>

                      <div className="space-y-1 text-xs text-[#475569] pt-2 border-t border-[#F1F5F9]">
                        <div className="flex justify-between">
                          <span>Hosted Checkout:</span>
                          <strong className="text-emerald-700">{p.hostedCheckout ? 'YES' : 'NO'}</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Native Subscriptions:</span>
                          <strong className="text-emerald-700">{p.nativeSubscriptions ? 'YES' : 'NO'}</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Mobile Money (Africa):</span>
                          <strong className={p.mobileMoneySupported ? 'text-emerald-700 font-bold' : 'text-[#94A3B8]'}>
                            {p.mobileMoneySupported ? 'SUPPORTED' : 'N/A'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PAID MONETIZATION MASTER SWITCH */}
          {activeTab === 'master-switch' && (
            <div className="space-y-6">
              <div className="p-6 bg-white border border-[#CBD5E1] rounded-2xl space-y-6 shadow-xs">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-[#E2E8F0]">
                  <div>
                    <h3 className="text-xl font-black text-[#0F172A] flex items-center gap-2">
                      <Power className="w-5 h-5 text-[#1E40AF]" />
                      <span>Executive Paid Monetization Control</span>
                    </h3>
                    <p className="text-sm text-[#64748B]">
                      Governs whether YamooPay operates in Free-Only Mode or processes commercial transactions.
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="text-xs text-[#64748B]">Current Operational Mode</span>
                    <div className="text-lg font-black text-[#1E40AF]">{monetizationControl.mode}</div>
                  </div>
                </div>

                {/* Mode Selector Radio Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    {
                      mode: 'OFF',
                      title: 'Mode OFF (Default Launch)',
                      desc: 'Zero paywalls. 100% free calculators, free APIs & accounts. Existing subscribers continue servicing normally.'
                    },
                    {
                      mode: 'INTERNAL_TEST',
                      title: 'Internal Test Mode',
                      desc: 'Checkouts visible only to authenticated internal testers and sandbox developer accounts.'
                    },
                    {
                      mode: 'LIMITED_ROLLOUT',
                      title: 'Limited Market Rollout',
                      desc: 'Controlled acquisition pilot for specific whitelisted countries and enterprise organizations.'
                    },
                    {
                      mode: 'ON',
                      title: 'Mode ON (Full Production)',
                      desc: 'Live global checkouts active across all configured payment providers and product tiers.'
                    },
                  ].map((item) => (
                    <button
                      key={item.mode}
                      onClick={() => handleSetMonetizationMode(item.mode as PaidMonetizationMode)}
                      className={`p-4 rounded-xl text-left border transition-all cursor-pointer ${
                        monetizationControl.mode === item.mode
                          ? 'bg-blue-50 border-[#1E40AF] ring-2 ring-[#1E40AF]/20'
                          : 'bg-[#F8FAFC] border-[#E2E8F0] hover:bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-bold text-sm text-[#0F172A]">{item.title}</span>
                        {monetizationControl.mode === item.mode && (
                          <CheckCircle2 className="w-4 h-4 text-[#1E40AF]" />
                        )}
                      </div>
                      <p className="text-xs text-[#64748B] leading-relaxed">{item.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Emergency Kill Switch */}
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h4 className="font-bold text-sm text-red-950 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <span>Payment Emergency Circuit Breaker</span>
                    </h4>
                    <p className="text-xs text-red-900 mt-0.5">
                      Instantly pause all checkout creation in case of downstream processor anomalies while preserving webhook servicing.
                    </p>
                  </div>

                  <button
                    onClick={handleToggleEmergencyKillSwitch}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                      monetizationControl.emergencyKillSwitch
                        ? 'bg-red-700 hover:bg-red-800 text-white'
                        : 'bg-white hover:bg-red-100 text-red-700 border border-red-300'
                    }`}
                  >
                    {monetizationControl.emergencyKillSwitch ? 'KILL SWITCH ENGAGED' : 'Engage Emergency Kill Switch'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: PRODUCTS & PLANS */}
          {activeTab === 'products-pricing' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Commercial Product Catalog & Pricing</h3>
                <p className="text-sm text-[#64748B]">
                  Immutable price definitions mapped to granular entitlement bundles.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {BILLING_PRODUCTS.map((prod) => (
                  <div key={prod.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{prod.name}</h4>
                        <span className="text-xs text-[#64748B] font-mono">{prod.key}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {prod.productType}
                      </span>
                    </div>

                    <p className="text-xs text-[#64748B] leading-relaxed">{prod.description}</p>

                    <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                      <div>
                        <span className="text-xs text-[#64748B]">Monthly Price:</span>
                        <strong className="block text-sm text-[#0F172A]">${prod.monthlyBasePriceUsd.toFixed(2)}</strong>
                      </div>
                      <div>
                        <span className="text-xs text-[#64748B]">Annual Discounted:</span>
                        <strong className="block text-sm text-[#059669]">${prod.annualBasePriceUsd.toFixed(2)}/yr</strong>
                      </div>
                    </div>

                    <div>
                      <span className="text-xs font-bold text-[#475569]">Granted Entitlements:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {prod.entitlements.map((ent) => (
                          <span key={ent} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-mono text-[10px]">
                            {ent}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: SUBSCRIPTIONS */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Canonical Subscriptions</h3>
                <p className="text-sm text-[#64748B]">
                  Authoritative multi-provider subscription state machine.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Customer</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Plan</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Provider</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Amount</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_SUBSCRIPTIONS.map((sub) => (
                      <tr key={sub.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3">
                          <div className="font-bold text-[#0F172A]">{sub.customerEmail}</div>
                          <div className="text-xs text-[#64748B] font-mono">{sub.customerId}</div>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-[#0F172A]">{sub.productName}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-bold rounded">
                            {sub.provider}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#0F172A]">{sub.amountFormatted}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                            {sub.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 5: TRANSACTIONS */}
          {activeTab === 'transactions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Payment Transactions & Checkout Sessions</h3>
                <p className="text-sm text-[#64748B]">
                  Verified server-side payments with idempotency keys and zero plaintext card storage.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Transaction ID</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Customer</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Amount</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Provider & Method</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_PAYMENT_TRANSACTIONS.map((txn) => (
                      <tr key={txn.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#1E40AF]">{txn.id}</td>
                        <td className="px-4 py-3 text-xs text-[#0F172A]">{txn.customerEmail}</td>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#0F172A]">${txn.amount.toFixed(2)} {txn.currency}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          <strong>{txn.provider}</strong> — {txn.paymentMethodSummary}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                            {txn.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 6: INVOICES */}
          {activeTab === 'invoices' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Invoices & Receipts</h3>
                <p className="text-sm text-[#64748B]">
                  Immutable billing documents with line-item detail and tax governance.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Invoice Number</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Customer</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Amount Paid</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Date</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_INVOICES.map((inv) => (
                      <tr key={inv.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#0F172A]">{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 text-xs text-[#0F172A]">{inv.customerEmail}</td>
                        <td className="px-4 py-3 font-mono text-xs font-bold text-[#059669]">${inv.amountPaid.toFixed(2)} {inv.currency}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 7: PROVIDERS */}
          {activeTab === 'providers' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Payment Provider Adapters & Capabilities</h3>
                <p className="text-sm text-[#64748B]">
                  Certified capability matrix preventing runtime feature assumption errors.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {PROVIDER_CAPABILITIES.map((p) => (
                  <div key={p.provider} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{p.name}</h4>
                        <span className="text-xs text-[#64748B] font-mono">{p.provider}</span>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                        {p.status}
                      </span>
                    </div>

                    <div className="text-xs text-[#475569] space-y-1 pt-2 border-t border-[#F1F5F9]">
                      <div><strong>Supported Currencies:</strong> {p.supportedCurrencies.join(', ')}</div>
                      <div><strong>Usage Metering:</strong> {p.usageBasedMetering ? 'Enabled' : 'Not Native'}</div>
                      <div><strong>Customer Portal:</strong> {p.customerPortal ? 'Supported' : 'YamooPay Native'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 8: COUPONS */}
          {activeTab === 'coupons' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Coupons & Promotional Discounts</h3>
                <p className="text-sm text-[#64748B]">
                  Audited promotional codes with anti-stacking controls and strict redemption limits.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {MOCK_COUPONS.map((c) => (
                  <div key={c.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <code className="text-base font-bold text-[#1E40AF] font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">
                          {c.code}
                        </code>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {c.status}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-xs text-[#64748B] pt-2 border-t border-[#F1F5F9]">
                      <span>Discount: <strong>{c.discountType === 'PERCENTAGE' ? `${c.discountValue}%` : `$${c.discountValue}`}</strong></span>
                      <span>Redemptions: <strong>{c.redemptionsCount} / {c.maxRedemptions}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 9: RECONCILIATION */}
          {activeTab === 'reconciliation' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Financial Reconciliation & Ledger Integrity</h3>
                <p className="text-sm text-[#64748B]">
                  Continuous comparison between provider settlement reports and YamooPay canonical transactions.
                </p>
              </div>

              <div className="space-y-3">
                {MOCK_RECONCILIATION_LOGS.map((log) => (
                  <div key={log.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-[#1E40AF]">{log.id}</span>
                        <h4 className="font-bold text-sm text-[#0F172A]">{log.type}</h4>
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                        {log.status}
                      </span>
                    </div>
                    <p className="text-xs text-[#475569] leading-relaxed">{log.description}</p>
                    <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#059669] font-medium">
                      <strong>Resolution:</strong> {log.resolutionAction}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
